// ============================================================
// SKILL TERMS - The named terms of an attack's modified skill:
// size, range, the weapon's own riders, hatred and frenzy, and the
// combat-action toggles folded into `attackMod`. Each builder returns
// the labelled rows the log's breakdown prints; `calcModifiedSkill`
// sums the same rows, so the roll and the breakdown cannot drift.
// Pure; no dice.
// ============================================================
import { SIZE_MODS } from './constants';
import { hasWS, getWSP } from './weaponSpecials';
import { stanceDeltas, guardedPenalty } from './actions/toggleEntries';
import type { Slot } from '../types';
import type { ModifierDetail } from './structuredNarrative';

/** Range modifier lookup (melee always 0) */
export const RANGE_MODS: Record<string, number> = { pointBlank: 30, short: 10, normal: 0, long: -10, extreme: -30 };

/** Range band display labels. Exported: the narrative names bands too, and
    two spellings of "Point Blank" in one log is how a table starts arguing. */
export const RANGE_LABELS: Record<string, string> = {
  pointBlank: 'Point Blank',
  short: 'Short',
  normal: 'Normal',
  long: 'Long',
  extreme: 'Extreme',
};

/** What a shot's range and weapon terms depend on. */
export interface ShotTerms {
  sp: string[];
  rangeBand: string;
  isMelee: boolean;
  aimed: boolean;
  removeLongRangePenalty: boolean;
  removeExtremeRangePenalty: boolean;
}

const row = (label: string, value: number): ModifierDetail => ({ label, value });
/** The rows whose condition held, in order (a falsy entry is a row that did not apply). */
const present = (...rows: (ModifierDetail | false | null | undefined)[]): ModifierDetail[] =>
  rows.filter((r): r is ModifierDetail => !!r);

/** Sum of the rows' values, left to right. */
export const sumRows = (rows: ModifierDetail[]): number => rows.reduce((s, m) => s + m.value, 0);

/** A modified skill is always a d100 target: never below 1, never above 100. */
export const clampSkill = (value: number): number => Math.min(Math.max(value, 1), 100);

/**
 * Size modifier - with Pestbane's cancellation shown as its own row (the
 * Marksman idiom: the penalty and its negation both visible). Pestbane
 * (v8.280.0): the vermin-hunter's eye - the PENALTY for small prey is
 * cancelled; bonuses for big targets stand untouched.
 */
export function sizeRows(sp: string[], targetSize: string): ModifierDetail[] {
  const sizeMod = SIZE_MODS[targetSize] || 0;
  return present(
    sizeMod !== 0 && row('Size (' + targetSize + ')', sizeMod),
    sizeMod < 0 && hasWS(sp, 'Pestbane') && row('Pestbane', -sizeMod),
  );
}

const isLongOrExtreme = (band: string) => band === 'long' || band === 'extreme';
const marksmanLong = (s: ShotTerms) => !s.isMelee && s.removeLongRangePenalty && s.rangeBand === 'long';
const marksmanExtreme = (s: ShotTerms) => !s.isMelee && s.removeExtremeRangePenalty && s.rangeBand === 'extreme';

/**
 * The range band's modifier after the Marksman-style talents, and
 * whether Telescopic (v8.280.0) zeroes what remains - it does the same
 * per-weapon, but only while AIMED.
 */
function rangeParts(s: ShotTerms): { band: number; telescopic: boolean } {
  const zeroed = s.isMelee || marksmanLong(s) || marksmanExtreme(s);
  return {
    band: zeroed ? 0 : RANGE_MODS[s.rangeBand] || 0,
    telescopic: !s.isMelee && s.aimed && hasWS(s.sp, 'Telescopic') && isLongOrExtreme(s.rangeBand),
  };
}

/** The range term the roll uses. */
export function rangeModFor(s: ShotTerms): number {
  const { band, telescopic } = rangeParts(s);
  return telescopic ? 0 : band;
}

/**
 * The range rows the breakdown prints, which always sum to `rangeModFor`:
 * the band's own penalty, then Marksman cancelling it (so the table sees
 * why the penalty is gone), then Telescopic cancelling what is left.
 * Marksman's row is the negated penalty itself, never a constant.
 */
export function rangeRows(s: ShotTerms): ModifierDetail[] {
  const { band, telescopic } = rangeParts(s);
  const raw = s.isMelee ? 0 : RANGE_MODS[s.rangeBand] || 0;
  const marksman = marksmanLong(s) || marksmanExtreme(s);
  return present(
    raw !== 0 && row('Range (' + (RANGE_LABELS[s.rangeBand] || s.rangeBand) + ')', raw),
    marksman && row('Marksman', -raw),
    telescopic && band !== 0 && row('Telescopic (Aim)', -band),
  );
}

const closeRange = (s: ShotTerms) => !s.isMelee && (s.rangeBand === 'pointBlank' || s.rangeBand === 'short');

/**
 * The weapon's own riders. Scatter: +10 to hit at Point Blank - applied
 * in this one funnel both probability engines share, since a to-hit bonus
 * in the roll and not the forecast is exactly the drift the dodge gate
 * suffered from. Brawling(X) (v8.280.0): +X inside short range, stacks
 * with Scatter. The weapon's innate typed modifier (v8.279.0) is distinct
 * from the per-attack `attackMod`. Official Accurate: an EXTRA +10 when
 * the shot was Aimed (v8.197.14), on top of the Aim toggle's own bonus;
 * Inaccurate suppresses every aim-derived benefit (v8.198.3).
 */
export function weaponRows(slot: Slot, s: ShotTerms): ModifierDetail[] {
  const brawling = closeRange(s) ? getWSP(s.sp, 'Brawling') : 0;
  const accurate = s.aimed && hasWS(s.sp, 'Accurate') && !hasWS(s.sp, 'Inaccurate');
  return present(
    hasWS(s.sp, 'Defensive') && row('Defensive', -10),
    hasWS(s.sp, 'Scatter') && !s.isMelee && s.rangeBand === 'pointBlank' && row('Scatter (Point Blank)', 10),
    brawling > 0 && row('Brawling (Close)', brawling),
    hasWS(s.sp, 'TwinLinked') && row('Twin-Linked', 20),
    !!slot.attackMod && row('Weapon (innate)', slot.attackMod),
    accurate && row('Accurate (Aim)', 10),
  );
}

/** Hatred and Frenzy (melee only) - Spiteful(X) (v8.281.0) sharpens the hatred term. */
export function meleeFuryRows(sp: string[], isMelee: boolean, hatred: boolean, frenzied: boolean): ModifierDetail[] {
  return present(
    hatred && isMelee && row('Hatred', 10 + getWSP(sp, 'Spiteful')),
    frenzied && isMelee && row('Frenzy', 10),
  );
}

/** The combat-action toggles, as the hook folds them into `attackMod`. */
export interface ToggleTerms {
  sp: string[];
  isMelee: boolean;
  isAimHalf: boolean;
  isAimFull: boolean;
  isAllOutAttack: boolean;
  isGuardedAttack: boolean;
  isCharge: boolean;
  isGangUp: boolean;
  hasBerserkCharge: boolean;
}

/** Rows that came out of `attackMod` are shown only when they moved it. */
const nonZero = (rows: ModifierDetail[]) => rows.filter((r) => r.value !== 0);

/**
 * Aim. Inaccurate: the Aim toggle contributed nothing to attackMod
 * (computeToggleModifier suppressed it), so the rows must not claim it -
 * but say WHY the aim went nowhere (a zero row, outside `attackMod`).
 */
function aimRows(t: ToggleTerms, aimDelta: number): ModifierDetail[] {
  if (hasWS(t.sp, 'Inaccurate')) {
    return t.isAimHalf || t.isAimFull ? [row('Inaccurate - Aim wasted', 0)] : [];
  }
  return nonZero(
    present(t.isAimHalf && row('Aim (Half)', 10 + aimDelta), t.isAimFull && row('Aim (Full)', 20 + aimDelta)),
  );
}

/**
 * The combat-action toggle rows. ORDER AND GATING MUST MATCH
 * `computeToggleModifier` - Aim is NOT melee-only, and a charge without
 * Berserk Charge is still +10. The stance family (v8.281.0) supplies the
 * same deltas the fold and the ledger use, from the one shared derivation.
 */
export function toggleRows(t: ToggleTerms): ModifierDetail[] {
  const stance = stanceDeltas(t.sp);
  const melee = (on: boolean) => on && t.isMelee;
  const charge = t.hasBerserkCharge ? row('Berserk Charge', 20 + stance.charge) : row('Charge', 10 + stance.charge);
  const meleeRows = present(
    melee(t.isAllOutAttack) && row('All-Out Attack', 20 + stance.allOut),
    melee(t.isGuardedAttack) && row('Guarded Attack', guardedPenalty(stance)),
    melee(t.isCharge) && charge,
    melee(t.isGangUp) && row('Gang Up', 10),
  );
  return [...aimRows(t, stance.aim), ...nonZero(meleeRows)];
}

/**
 * Called Shot. The talent REDUCES the penalty rather than adding a bonus
 * on top of it, which is why the entries are the split halves of one
 * number - together they are exactly what the toggle contributed. Capped
 * at the penalty itself: the talent cannot turn a called shot into a
 * bonus, and `computeToggleModifier` floors it at zero too. The target's
 * own share (a `called` exposure, v8.368.2) is its own row; the talent's
 * is the rest.
 */
export function calledShotRows(
  isMelee: boolean,
  reductionIn: number,
  exposureIn: number,
  ignored = false,
): ModifierDetail[] {
  // The Controls switch (v8.415.0) waives all of it, and is credited alone.
  if (ignored) return [row('Called Shot', -20), row('Called Shot penalty ignored', 20)];
  const reduction = Math.min(Math.max(reductionIn || 0, 0), 20);
  const exposed = Math.min(Math.max(exposureIn || 0, 0), reduction);
  return nonZero(
    present(
      row('Called Shot', -20),
      reduction - exposed > 0 && row(isMelee ? 'Precise Blow' : 'Deadeye Shot', reduction - exposed),
      exposed > 0 && row('Target exposed to called shots', exposed),
    ),
  );
}
